import { useEffect, useRef } from 'react';
import { useThree, useFrame, invalidate, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { editorStore } from '../../store/editorStore';
import { observer } from 'mobx-react-lite';
import { projectToWorld } from '../FloorPlan/projectToWorld';
import { floorplannerStore } from '../../store/floorplannerStore';
import { AlignmentLineType, AreaSymbolType, WallConnectionEnd } from '../../types/floorplannerTypes';
import { updateAlignmentLinesForObject } from '../FloorPlan/updateAligmentLines';
import { renderStore } from '../../store/renderStore';

const AREA_PREVIEW_ID = "__area-construction-preview__";

const AreaConstructionPlane = observer(() => {
  const { areaConstructionMode, areaFillConstructionMode, areaConstructionModeAddPoint, view3D } = editorStore;
  const { gl, camera, scene } = useThree();
  const areaSymbol = useRef(undefined as string | undefined);
  const lastClickPosition = useRef(undefined as THREE.Vector2 | undefined);
  const constructionActive = areaConstructionMode || areaFillConstructionMode;
  const constructionSymbolType = areaFillConstructionMode ? "areaFill" : "area";

  const finishConstructionMode = () => {
    if (areaFillConstructionMode) {
      editorStore.setAreaFillConstructionMode(false);
    } else {
      editorStore.setAreaConstructionMode(false);
    }
  };

  const updateFirstPointAlignmentPreview = (point: THREE.Vector2, shiftPressed: boolean) => {
    if (shiftPressed) {
      renderStore.clearAlignmentLines();
      return;
    }

    updateAlignmentLinesForObject({
      id: AREA_PREVIEW_ID,
      type: "singleLine",
      start: point.clone(),
      end: point.clone(),
    }, point.x, point.y, "end");
  };

  const snapToAlignmentLines = (point: THREE.Vector2, shiftPressed: boolean): THREE.Vector2 => {
    if (shiftPressed) {
      return point.clone();
    }

    let snappedX: number | undefined;
    let snappedY: number | undefined;
    let bestXDistance = Infinity;
    let bestYDistance = Infinity;
    const snapTolerance = editorStore.interactionSnappingTolerance();

    const checkLine = (line: AlignmentLineType) => {
      if (line.axis === "x") {
        const distance = Math.abs(point.x - line.hit.x);
        if (distance < snapTolerance && distance < bestXDistance) {
          snappedX = line.hit.x;
          bestXDistance = distance;
        }
      } else if (line.axis === "y") {
        const distance = Math.abs(point.y - line.hit.y);
        if (distance < snapTolerance && distance < bestYDistance) {
          snappedY = line.hit.y;
          bestYDistance = distance;
        }
      }
    };

    renderStore.alignmentLinesApproxMap.forEach(checkLine);
    renderStore.alignmentLinesMap.forEach(checkLine);

    const snappedPoint = point.clone();
    if (snappedX !== undefined) {
      snappedPoint.x = snappedX;
    }
    if (snappedY !== undefined) {
      snappedPoint.y = snappedY;
    }

    return snappedPoint;
  };

  const getSnappedFirstPoint = (point: THREE.Vector2, shiftPressed: boolean): THREE.Vector2 => {
    updateFirstPointAlignmentPreview(point, shiftPressed);
    return snapToAlignmentLines(point, shiftPressed);
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (constructionActive) {
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const posVector = new THREE.Vector2(pos[0], pos[1]);
      if (areaSymbol.current) {
        addNewPoint(posVector);
      } else {
        const snappedFirstPoint = getSnappedFirstPoint(posVector, event.shiftKey);
        createNewAreaSymbol(snappedFirstPoint);
        lastClickPosition.current = snappedFirstPoint.clone();
        return;
      }
      lastClickPosition.current = posVector
    }
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (constructionActive && !editorStore.view3D) {
      document.body.classList.remove("cursor-vector")
      document.body.style.cursor = 'crosshair';
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const pointerPosition = new THREE.Vector2(pos[0], pos[1]);

      if (!areaSymbol.current) {
        updateFirstPointAlignmentPreview(pointerPosition, event.shiftKey);
      }

      if (areaSymbol.current) {
        const area = floorplannerStore.symbolsMap.get(areaSymbol.current) as AreaSymbolType;
        if (area) {
          const start = area.vertices[area.vertices.length - 2];
          // Work in world coordinates for snapping, then convert back to relative
          const worldEndPos = pointerPosition.clone();
          const worldStartPos = start.clone().add(area.position as THREE.Vector2);
          let end = worldEndPos.clone().sub(area.position as THREE.Vector2);

          // SNAPPING PRIORITY ORDER for area construction:
          // 1. Wall edge corners (for precise area vertices at wall edges) - HIGHEST PRIORITY
          // 2. 45-degree angle snapping (when within 20 degrees of snap angle)
          // 3. Alignment lines - LOWEST PRIORITY

          let snappedWallAngle: number | undefined;
          let objectSnapApplied = false;
          const snapTolerance = editorStore.interactionSnappingTolerance();

          // PRIORITY 1: Wall edge corner snapping (highest priority)
          // Allows area vertices to snap to wall edges for precise placement
          if (!editorStore.isShiftPressed && !editorStore.isAltPressed) {
            const edgeSnapCandidate = floorplannerStore.getEdgeSnappingPositionForDrag(area.id, "end", worldEndPos);
            if (edgeSnapCandidate?.snappingPosition &&
              worldEndPos.distanceTo(edgeSnapCandidate.snappingPosition) < snapTolerance) {
              // Convert snapped world position back to relative coordinates
              end = edgeSnapCandidate.snappingPosition.clone().sub(area.position as THREE.Vector2);
              objectSnapApplied = true;
            }
          } else if (editorStore.isAltPressed && !editorStore.isShiftPressed) {
            // Center mode: check for center-based snapping
            const centerSnapPoint = floorplannerStore.getSnappingPositionForDrag(area.id, WallConnectionEnd, worldEndPos);
            if (centerSnapPoint && worldEndPos.distanceTo(centerSnapPoint) < snapTolerance) {
              end = centerSnapPoint.clone().sub(area.position as THREE.Vector2);
              objectSnapApplied = true;
            }
          }

          // PRIORITY 2: 45-degree angle snapping (only if no edge snap was applied)
          let angleSnapApplied = false;
          if (!objectSnapApplied && !editorStore.isShiftPressed) {
            // Calculate the angle between the start and current mouse position
            const deltaX = end.x - start.x;
            const deltaY = end.y - start.y;
            const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI); // Convert to degrees

            // Get the current distance between the start and end position
            const currentDistance = start.distanceTo(end);

            // Normalize the angle to a positive value between 0 and 360
            const normalizedAngle = (angle + 360) % 360;

            // Snap the angle to the nearest 45 degrees
            const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
            let closestSnapAngle = snapAngles.reduce((prev, curr) => Math.abs(curr - normalizedAngle) < Math.abs(prev - normalizedAngle) ? curr : prev);

            // Convert the snapped angle to radians
            const radianSnapAngle = closestSnapAngle * (Math.PI / 180);

            // Calculate the snapped end position (along the nearest 45-degree line)
            const snapEndX = start.x + currentDistance * Math.cos(radianSnapAngle);
            const snapEndY = start.y + currentDistance * Math.sin(radianSnapAngle);

            // Calculate the distance between the current mouse position and the snapped position
            const snapDistance = end.distanceTo(new THREE.Vector2(snapEndX, snapEndY));

            // If the distance to the snapped angle is less than 0.2, snap to the closest 45-degree angle
            if (snapDistance < editorStore.interactionSnappingTolerance(0.2)) {
              end.set(snapEndX, snapEndY);
              snappedWallAngle = closestSnapAngle === 360 ? 0 : closestSnapAngle;
              angleSnapApplied = true;
            }
          }

          // PRIORITY 3: Alignment snapping
          // Can work alongside angle snapping for the appropriate axis
          // Skip only if edge snap was applied (that takes full priority)
          // Note: We use direct alignment line checking here instead of applyAlignmentAfterSnap
          // because that function relies on findObjectId which doesn't work for symbols
          if (!objectSnapApplied && !editorStore.isShiftPressed) {
            const worldEnd = end.clone().add(area.position as THREE.Vector2);
            const worldStart = start.clone().add(area.position as THREE.Vector2);

            // Determine which axes are appropriate for alignment based on snapped angle
            // Similar logic to shouldApplyXAlignment/shouldApplyYAlignment in applyAlignmentAfterSnap.ts
            let allowXAlignment = true;
            let allowYAlignment = true;

            if (angleSnapApplied && snappedWallAngle !== undefined) {
              // X-alignment aligns to vertical lines (should skip if angle is nearly vertical: 90° or 270°)
              const diffFrom90 = Math.abs(snappedWallAngle - 90);
              const diffFrom270 = Math.abs(snappedWallAngle - 270);
              const minDiffX = Math.min(diffFrom90, diffFrom270);
              allowXAlignment = minDiffX > 20;

              // Y-alignment aligns to horizontal lines (should skip if angle is nearly horizontal: 0°, 180°, 360°)
              const diffFrom0 = Math.min(snappedWallAngle, 360 - snappedWallAngle);
              const diffFrom180 = Math.abs(snappedWallAngle - 180);
              const minDiffY = Math.min(diffFrom0, diffFrom180);
              allowYAlignment = minDiffY > 20;
            }

            // Direct alignment snapping: check alignment lines from renderStore
            // and snap to them if within tolerance
            let snappedToAlignmentX = false;
            let snappedToAlignmentY = false;
            let alignmentTargetX: number | undefined;
            let alignmentTargetY: number | undefined;

            // Check approximate alignment lines (these are generated by updateAlignmentLinesForObject)
            const checkAlignmentLines = (lines: Iterable<AlignmentLineType>) => {
              for (const line of lines) {
                if (line.axis === 'x' && !snappedToAlignmentX && allowXAlignment) {
                  // X-axis alignment line (vertical line)
                  const distance = Math.abs(worldEnd.x - line.hit.x);
                  if (distance < snapTolerance) {
                    alignmentTargetX = line.hit.x;
                    snappedToAlignmentX = true;
                  }
                } else if (line.axis === 'y' && !snappedToAlignmentY && allowYAlignment) {
                  // Y-axis alignment line (horizontal line)
                  const distance = Math.abs(worldEnd.y - line.hit.y);
                  if (distance < snapTolerance) {
                    alignmentTargetY = line.hit.y;
                    snappedToAlignmentY = true;
                  }
                }
              }
            };

            // Check both approximate and exact alignment lines
            checkAlignmentLines(renderStore.alignmentLinesApproxMap.values());
            checkAlignmentLines(renderStore.alignmentLinesMap.values());

            if (snappedToAlignmentX || snappedToAlignmentY) {
              if (angleSnapApplied && snappedWallAngle !== undefined) {
                // When angle is locked, adjust position along the angle ray to hit the alignment
                const radianSnapAngle = snappedWallAngle * (Math.PI / 180);
                const cosAngle = Math.cos(radianSnapAngle);
                const sinAngle = Math.sin(radianSnapAngle);

                // For diagonal angles, we prioritize one axis (pick the closest alignment)
                // For axis-aligned angles, only one alignment is possible
                let newDistance: number | undefined;

                if (snappedToAlignmentX && Math.abs(cosAngle) > 0.01) {
                  // Calculate distance to hit X alignment: start.x + d * cos(angle) = targetX
                  const distanceForX = (alignmentTargetX! - worldStart.x) / cosAngle;
                  if (distanceForX > 0) {
                    newDistance = distanceForX;
                  }
                }

                if (snappedToAlignmentY && Math.abs(sinAngle) > 0.01) {
                  // Calculate distance to hit Y alignment: start.y + d * sin(angle) = targetY
                  const distanceForY = (alignmentTargetY! - worldStart.y) / sinAngle;
                  if (distanceForY > 0) {
                    // If we already have a distance from X, pick the closer adjustment
                    if (newDistance === undefined) {
                      newDistance = distanceForY;
                    } else {
                      // Both alignments are possible - pick the one with smaller distance change
                      const currentDistance = worldStart.distanceTo(worldEnd);
                      const xDiff = Math.abs(newDistance - currentDistance);
                      const yDiff = Math.abs(distanceForY - currentDistance);
                      if (yDiff < xDiff) {
                        newDistance = distanceForY;
                      }
                    }
                  }
                }

                if (newDistance !== undefined && newDistance > 0) {
                  // Recalculate end position along the angle ray
                  worldEnd.x = worldStart.x + newDistance * cosAngle;
                  worldEnd.y = worldStart.y + newDistance * sinAngle;
                }
              } else {
                // No angle snap - apply alignment directly to each axis
                if (snappedToAlignmentX && alignmentTargetX !== undefined) {
                  worldEnd.x = alignmentTargetX;
                }
                if (snappedToAlignmentY && alignmentTargetY !== undefined) {
                  worldEnd.y = alignmentTargetY;
                }
              }

              // Convert snapped world position back to relative coordinates
              end = worldEnd.clone().sub(area.position as THREE.Vector2);
            }
          }

          const vertices = [...area.vertices];
          vertices[vertices.length - 1] = end;
          floorplannerStore.updateSymbolProperty(areaSymbol.current, "vertices", vertices);

          // Update alignment lines using world coordinates
          // Create a pseudo-line object representing the current area edge for alignment line generation
          const worldEnd = end.clone().add(area.position as THREE.Vector2);
          const pseudoLineObject = {
            id: area.id,
            type: 'area',
            start: worldStartPos.clone(),
            end: worldEnd.clone(),
          };
          updateAlignmentLinesForObject(pseudoLineObject, worldEnd.x, worldEnd.y, "end");
        }
      }
    }
  };

  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    if (constructionActive) {
      // Get the click position in world coordinates
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const clickPosition = new THREE.Vector2(pos[0], pos[1]);

      if (areaSymbol.current) {
        const area = floorplannerStore.symbolsMap.get(areaSymbol.current) as AreaSymbolType;
        if (area) {
          const areaPosition = area.position;
          const vertices = area.vertices;

          // Get the last vertex adjusted for the area's position
          const lastVertex = vertices[vertices.length - 1].clone().add(areaPosition as THREE.Vector2);

          // Calculate the distance between the click and the first vertex
          const distance = clickPosition.distanceTo(lastVertex);

          // If the click is close enough to the first vertex, exit construction mode
          if (distance < editorStore.interactionSnappingTolerance()) {
            // Exit area construction mode
            finishConstructionMode();
            // Additional cleanup if necessary
            // Optionally, close the area by ensuring the first and last vertices are the same
            floorplannerStore.updateSymbolProperty(areaSymbol.current, "vertices", [...vertices.slice(0, vertices.length - 1), vertices[0]]);
          }
        }
      }
    }
  };

  const createNewAreaSymbol = (position: THREE.Vector2) => {
    if (!areaSymbol.current) {
      const start = new THREE.Vector2(0, 0);
      const end = new THREE.Vector2(0, 0);
      // Skip committing undo for the preview; record the finished polygon below.
      areaSymbol.current = floorplannerStore.addSymbol(
        constructionSymbolType,
        [position.x, position.y],
        {
          vertices: [start, end],
          selectable: true,
        },
        { commitUndo: false },
      );
      // addSymbol still records into a transaction when commitUndo is false;
      // discard it so mid-draw edits are not batched into an open undo entry.
      floorplannerStore.cancelUndoTransaction();
    }
  }

  const addNewPoint = (pos: THREE.Vector2) => {
    if (areaSymbol.current) {
      const area = floorplannerStore.symbolsMap.get(areaSymbol.current) as AreaSymbolType;
      if (area) {
        const deltaPos = pos.clone().sub(area.position);
        const vertices = area.vertices;
        const lastVertex = vertices[vertices.length - 2];
        const distance = lastVertex.distanceTo(deltaPos);
        if (distance > 0.1) {
          floorplannerStore.updateSymbolProperty(areaSymbol.current, "vertices", [...vertices, deltaPos]);
        }
        // If the added point is near the first point, exit the area construction mode
        if (vertices[0].distanceTo(area.vertices[vertices.length - 1]) < 0.1) {
          finishConstructionMode();
        }
      }
    }
  };

  useEffect(() => {
    if (areaSymbol.current && areaConstructionModeAddPoint) {
      editorStore.setAreaConstructionModeAddPoint(false);
      const area = floorplannerStore.symbolsMap.get(areaSymbol.current) as AreaSymbolType;
      if (area) {
        lastClickPosition.current = area.vertices[area.vertices.length - 1].clone();
      }
    }
  }, [areaConstructionModeAddPoint]);

  useEffect(() => {
    const completedAreaId = areaSymbol.current;
    // Do some checking when we exit the area construction mode
    if (!constructionActive && areaSymbol.current) {
      const area = floorplannerStore.symbolsMap.get(areaSymbol.current) as AreaSymbolType;
      if (area) {
        // Remove last vertex (temporary point used during construction)
        const vertices = area.vertices.slice(0, area.vertices.length - 1);
        floorplannerStore.updateSymbolProperty(areaSymbol.current, "vertices", vertices);
        if (vertices.length < 3) {
          // Force: a lease hand-off can disarm the tool mid-draw, and the
          // unfinished area must still be discarded rather than left on canvas.
          floorplannerStore.removeSymbol(areaSymbol.current, { force: true });
        } else {
          // Close the area by connecting the last point to the first point
          if (vertices[0].distanceTo(vertices[vertices.length - 1]) > 0.01) {
            floorplannerStore.updateSymbolProperty(areaSymbol.current, "vertices", [...vertices, vertices[0]]);
          } else {
            // Snap last point to the first point
            vertices[vertices.length - 1] = vertices[0];
            floorplannerStore.updateSymbolProperty(areaSymbol.current, "vertices", vertices);
          }
          // Commit the deferred add from createNewAreaSymbol with final geometry.
          floorplannerStore.beginUndoTransaction("addArea");
          floorplannerStore.recordSymbolChange(areaSymbol.current, "add");
          floorplannerStore.pushToUndoStack();
          const completedArea = floorplannerStore.symbolsMap.get(areaSymbol.current);
          if (completedArea?.type === "areaFill") {
            editorStore.clearSelections();
            editorStore.addSelection(completedArea);
            floorplannerStore.updateSymbolProperty(completedArea.id, "selected", true);
            editorStore.requestOpenFillProperties();
          }
        }
      }
    }
    // Clear alignment lines when entering or exiting construction mode
    renderStore.clearAlignmentLines();
    editorStore.setAreaEditingLength(undefined);
    areaSymbol.current = undefined;
    lastClickPosition.current = undefined;
    if (constructionActive) {
      document.body.classList.remove("cursor-vector")
      document.body.style.cursor = 'crosshair';
      floorplannerStore.setBlockDirty(true);
    } else {
      document.body.style.cursor = 'auto';
      floorplannerStore.setBlockDirty(false);
      if (completedAreaId) {
        floorplannerStore.setDirty();
      }
    }
  }, [constructionActive, areaFillConstructionMode]);

  return (
    <mesh
      position={[0, 0, .2]}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      //onDoubleClick={handleDoubleClick}
      userData={{ id: "area-construction-plane" }}
    >
      <planeGeometry args={[1000, 1000]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
});

export default AreaConstructionPlane;
